import { orgAPI } from "@/api/api";
import {
  createTranscriptionStreamWebSocket,
  TranscriptionClientFrame,
  TranscriptionServerFrame,
} from "@cohesive/api-client";
import createDebug from "debug";
import {
  CaptureChunk,
  CaptureSource,
  chunkDurationMs,
  createCaptureSource,
} from "./captureSource";
import { PcmRingBuffer } from "./pcmRingBuffer";
import { TranscriptAccumulator } from "./transcriptAccumulator";
import type { DraftStorage, VoiceDraftV1 } from "./voiceDraft";

const debug = createDebug("cohesive:voice-session");

/**
 * VoiceSessionController — the engine behind the mobile voice screen.
 *
 * Owns capture (native plugin or Web Audio), the transcription WebSocket
 * lifecycle, session *chaining* past the server's 30-minute cap, reconnect
 * with faster-than-realtime replay from a bounded PCM ring buffer, the
 * committed+live transcript accumulator, and crash-safe draft persistence.
 *
 * The invariants, in order of importance:
 *  1. Text shown on screen is NEVER lost except by explicit user discard.
 *     Capture starts before (and outlives) any socket; every session's text
 *     is folded into the accumulator before its socket is abandoned; the
 *     draft persists on a throttle and clears only on delivery/discard.
 *  2. Prefer duplicating ~1s of audio at a drop seam over losing any
 *     (`dropReplayRewindMs` + word-overlap dedupe in the accumulator).
 *  3. The mic never dies for socket reasons: connect/ready failures and
 *     drops keep capturing into the ring and retry in the background, until
 *     the ring would overflow — then recording pauses honestly.
 *
 * Framework-agnostic: all I/O (socket, capture, storage, clock) is injected
 * so the whole state machine unit-tests with fakes. React consumption goes
 * through useVoiceSession (useSyncExternalStore over subscribe/getSnapshot).
 */

export type VoicePhase =
  | "idle"
  | "connecting"
  | "live"
  | "reconnecting"
  | "rotating" // internal: pre-warm session swap; UI renders it as "live"
  | "paused"
  | "finalizing"
  | "review"
  | "error";

export type PauseReason = "user" | "buffer_exhausted" | "interruption" | null;

export interface VoiceSessionSnapshot {
  phase: VoicePhase;
  /** Accumulated transcript: committed segments + seam-deduped live tail. */
  fullText: string;
  /** Recorded audio time in seconds (paused gaps excluded). */
  elapsedSec: number;
  isMuted: boolean;
  reconnectAttempt: number;
  /** While disconnected: seconds of ring buffer left before auto-pause. */
  bufferSecondsRemaining: number | null;
  pauseReason: PauseReason;
  error: string | null;
}

export interface VoiceSessionConfig {
  /** Mirror of the server's 30-minute maxSessionDuration. */
  maxSessionSec: number;
  /** Rotate at the next detected silence past this per-session wall time. */
  softRotateSec: number;
  /** Rotate unconditionally past this (before the server cuts us off). */
  hardRotateSec: number;
  silenceRms: number;
  silenceWindowMs: number;
  /** Budget for socket construction + the server `ready` frame. */
  connectTimeoutMs: number;
  /** stop → session_final budget (server flush 2s + polish up to 3s). */
  finalizeTimeoutMs: number;
  reconnectBackoffMs: number[];
  ringBufferMs: number;
  /** Pause capture this early so eviction never eats unsent audio. */
  exhaustionMarginMs: number;
  /** Backpressure gate for replay: don't grow ws.bufferedAmount past this. */
  replayMaxBufferedBytes: number;
  /** Unexpected-drop replay rewinds this far behind the last transcript. */
  dropReplayRewindMs: number;
  persistThrottleMs: number;
  pingIntervalMs: number;
  /** Retry cadence when a pre-warm rotation fails while the old socket lives. */
  rotateRetryMs: number;
  /**
   * Reject `start()` when the FIRST connect fails rather than retrying, so
   * the caller can degrade to another engine. Reconnects after a successful
   * first connect are unaffected.
   */
  failFastOnFirstConnect: boolean;
  /**
   * Stop the recording once this much audio has been captured. Undefined =
   * unbounded (mobile); the composer sets it so dictation keeps its cap even
   * though session chaining removes the server's 30-minute ceiling.
   */
  maxTotalRecordingSec?: number;
}

/** `start()` rejected because the first connect failed (fail-fast mode). */
export class VoiceConnectFailedError extends Error {
  constructor() {
    super("voice session connect failed");
    this.name = "VoiceConnectFailedError";
  }
}

/** `start()` rejected because the user cancelled while it was connecting. */
export class VoiceCancelledError extends Error {
  constructor() {
    super("voice session cancelled");
    this.name = "VoiceCancelledError";
  }
}

export const DEFAULT_VOICE_SESSION_CONFIG: VoiceSessionConfig = {
  maxSessionSec: 1800,
  softRotateSec: 1500,
  hardRotateSec: 1680,
  silenceRms: 0.015,
  silenceWindowMs: 700,
  connectTimeoutMs: 5000,
  finalizeTimeoutMs: 5000,
  reconnectBackoffMs: [1000, 2000, 4000, 8000, 15000],
  ringBufferMs: 300_000,
  exhaustionMarginMs: 10_000,
  replayMaxBufferedBytes: 262_144,
  dropReplayRewindMs: 1000,
  persistThrottleMs: 1500,
  pingIntervalMs: 15_000,
  rotateRetryMs: 60_000,
  failFastOnFirstConnect: false,
};

/** Thin socket seam so tests can script server frames. */
export interface VoiceSocket {
  sendJson(frame: TranscriptionClientFrame): void;
  sendAudio(chunk: ArrayBuffer): void;
  readonly bufferedAmount: number;
  readonly isOpen: boolean;
  close(): void;
  setHandlers(handlers: {
    onFrame: (frame: TranscriptionServerFrame) => void;
    onClose: () => void;
  }): void;
}

/** Injectable timer/now seam (fake in tests). */
export interface VoiceClock {
  now(): number;
  setTimeout(fn: () => void, ms: number): unknown;
  clearTimeout(id: unknown): void;
  setInterval(fn: () => void, ms: number): unknown;
  clearInterval(id: unknown): void;
}

const realClock: VoiceClock = {
  now: () => Date.now(),
  setTimeout: (fn, ms) => setTimeout(fn, ms),
  clearTimeout: (id) => clearTimeout(id as ReturnType<typeof setTimeout>),
  setInterval: (fn, ms) => setInterval(fn, ms),
  clearInterval: (id) => clearInterval(id as ReturnType<typeof setInterval>),
};

export interface VoiceSessionDeps {
  createSocket: () => Promise<VoiceSocket>;
  createCapture: () => Promise<CaptureSource>;
  storage: DraftStorage;
  clock?: VoiceClock;
  config?: Partial<VoiceSessionConfig>;
}

/** Wraps a live WebSocket from the api-client factory. */
function wrapWebSocket(ws: WebSocket): VoiceSocket {
  let frameHandler: ((frame: TranscriptionServerFrame) => void) | null = null;
  let closeHandler: (() => void) | null = null;
  let closed = false;
  const emitClose = () => {
    if (closed) return;
    closed = true;
    closeHandler?.();
  };
  ws.onmessage = (event: MessageEvent) => {
    if (typeof event.data !== "string") return;
    try {
      frameHandler?.(JSON.parse(event.data) as TranscriptionServerFrame);
    } catch {
      // Ignore malformed frames.
    }
  };
  ws.onclose = emitClose;
  ws.onerror = () => {
    // An errored socket that never opens fires no close in some engines.
    if (ws.readyState === WebSocket.CLOSED) emitClose();
  };
  return {
    sendJson: (frame) => {
      if (ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify(frame));
    },
    sendAudio: (chunk) => {
      if (ws.readyState === WebSocket.OPEN) ws.send(chunk);
    },
    get bufferedAmount() {
      return ws.bufferedAmount;
    },
    get isOpen() {
      return ws.readyState === WebSocket.OPEN;
    },
    close: () => {
      if (
        ws.readyState !== WebSocket.CLOSED &&
        ws.readyState !== WebSocket.CLOSING
      ) {
        ws.close();
      }
    },
    setHandlers: (handlers) => {
      frameHandler = handlers.onFrame;
      closeHandler = handlers.onClose;
    },
  };
}

export async function createDefaultVoiceSocket(): Promise<VoiceSocket> {
  const [url, token] = await Promise.all([
    orgAPI.transcriptionStream.getStreamUrl(),
    orgAPI.getToken(),
  ]);
  return wrapWebSocket(createTranscriptionStreamWebSocket(url, token));
}

export function defaultVoiceSessionDeps(
  storage: DraftStorage,
): VoiceSessionDeps {
  return {
    createSocket: createDefaultVoiceSocket,
    createCapture: createCaptureSource,
    storage,
  };
}

interface ActiveSession {
  socket: VoiceSocket;
  /** Guards stale async callbacks after a swap. */
  seq: number;
  connectedAtWall: number;
  /** Capture-timeline ms of the first audio this session was fed. */
  startCursorMs: number;
  /** Capture-timeline high-water mark of audio sent to this session. */
  sentUpToMs: number;
  /** `sentUpToMs` captured at the last transcript frame — drop-replay rewinds
   * from here (minus dropReplayRewindMs). */
  lastTranscriptSentUpToMs: number;
  gotAnyTranscript: boolean;
}

interface DrainingSession {
  socket: VoiceSocket;
  seq: number;
  provisionalIndex: number | null;
  timer: unknown;
}

function makeDraftId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

const LEVEL_HISTORY_LENGTH = 160;
/** Aggregate level samples to ~12.5Hz regardless of chunk cadence. */
const LEVEL_SAMPLE_MS = 80;

export class VoiceSessionController {
  private readonly clock: VoiceClock;
  private readonly config: VoiceSessionConfig;
  private readonly storage: DraftStorage;

  private phase: VoicePhase = "idle";
  private pauseReason: PauseReason = null;
  private errorMessage: string | null = null;
  private reconnectAttempt = 0;
  private muted = false;
  private stopRequested = false;
  private disposed = false;

  private capture: CaptureSource | null = null;
  private readonly ring: PcmRingBuffer;
  readonly accumulator = new TranscriptAccumulator();

  private active: ActiveSession | null = null;
  /** Socket awaiting `ready`; closed by teardown so a cancel is immediate. */
  private connectingSocket: VoiceSocket | null = null;
  private draining: DrainingSession | null = null;
  private sessionSeq = 0;
  /** Where the next session's replay starts (capture-timeline ms). */
  private nextReplayFromMs = 0;
  /** Captured-but-unsent chunks queued for the active socket. */
  private pending: CaptureChunk[] = [];

  private recordedMs = 0;
  private draftId = makeDraftId();
  private workspaceScopeId: string | undefined;
  private reviewText: string | null = null;

  // Timers.
  private pingTimer: unknown = null;
  private rotateWatchTimer: unknown = null;
  private reconnectTimer: unknown = null;
  private finalizeTimer: unknown = null;
  private pumpRetryTimer: unknown = null;
  private persistTimer: unknown = null;
  private rotateRetryTimer: unknown = null;

  // Audio level state (read via getAudioLevels, outside React).
  private level = 0;
  private levelHistory: number[] = [];
  private levelWindowMs = 0;
  private levelWindowPeak = 0;
  private rmsSamples: { at: number; rms: number }[] = [];

  // Subscription plumbing.
  private listeners = new Set<() => void>();
  private snapshotCache: VoiceSessionSnapshot | null = null;
  private lastNotifiedSec = -1;

  constructor(deps: VoiceSessionDeps) {
    this.clock = deps.clock ?? realClock;
    this.config = { ...DEFAULT_VOICE_SESSION_CONFIG, ...deps.config };
    this.storage = deps.storage;
    this.createSocket = deps.createSocket;
    this.createCapture = deps.createCapture;
    this.ring = new PcmRingBuffer(this.config.ringBufferMs);
  }

  private readonly createSocket: () => Promise<VoiceSocket>;
  private readonly createCapture: () => Promise<CaptureSource>;

  // ----------------------------------------------------------------- public

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  getSnapshot = (): VoiceSessionSnapshot => {
    if (!this.snapshotCache) {
      this.snapshotCache = {
        phase: this.phase,
        fullText: this.accumulator.fullText(),
        elapsedSec: Math.floor(this.recordedMs / 1000),
        isMuted: this.muted,
        reconnectAttempt: this.reconnectAttempt,
        bufferSecondsRemaining: this.bufferSecondsRemaining(),
        pauseReason: this.pauseReason,
        error: this.errorMessage,
      };
    }
    return this.snapshotCache;
  };

  /** Live audio level for the waveform — poll from rAF, not React state. */
  getAudioLevels(): { level: number; history: readonly number[] } {
    return { level: this.level, history: this.levelHistory };
  }

  isDisposed(): boolean {
    return this.disposed;
  }

  getReviewText(): string {
    return this.reviewText ?? this.accumulator.fullText();
  }

  updateReviewText(text: string): void {
    this.reviewText = text;
    this.schedulePersist();
  }

  /**
   * Begin a recording. Resolves once capture is running and — in
   * `failFastOnFirstConnect` mode — the first session is live.
   *
   * Rejects only in fail-fast mode, with {@link VoiceConnectFailedError} when
   * the very first connect fails, or {@link VoiceCancelledError} when the user
   * cancelled while it was in flight. The web composer uses that to fall back
   * to the legacy HTTP engine; mobile leaves the mode off, because there is
   * nothing to fall back to and retrying is strictly better.
   */
  async start(opts: { workspaceScopeId?: string } = {}): Promise<void> {
    if (this.phase !== "idle" || this.disposed) return;
    this.workspaceScopeId = opts.workspaceScopeId;
    this.draftId = makeDraftId();
    this.draftCreatedAt = this.clock.now();
    this.draftOwned = true;
    this.stopRequested = false;
    this.errorMessage = null;
    // Claim a generation before the first await. Everything that invalidates
    // a start — cancel, dispose — bumps this, so each await below can tell
    // whether it is still the owner when it resolves.
    const generation = ++this.sessionSeq;
    this.setPhase("connecting");

    // Capture first: once the mic is rolling, no socket problem can lose
    // audio — it all lands in the ring buffer.
    let capture: CaptureSource;
    try {
      capture = await this.createCapture();
    } catch (captureError) {
      debug("capture create failed", captureError);
      // A cancel may have landed while this was failing. Reporting a mic
      // error then would be wrong twice over: it surfaces an error for a
      // take the user dismissed, and it would stomp the state of a newer
      // start that already owns this controller.
      if (this.isSuperseded(generation)) return this.abandonStart(null);
      this.failCapture();
      return;
    }
    // A cancel (Quick Voice dismiss, unmount, Strict Mode remount) can land
    // while the permission prompt or getUserMedia is pending. Without these
    // checks the resolved source would keep running with no owner and the
    // start would go on to connect a session the user already dismissed.
    if (this.isSuperseded(generation)) {
      return this.abandonStart(capture);
    }

    this.capture = capture;
    try {
      await capture.start({
        onChunk: (chunk) => this.handleChunk(chunk),
        onInterruption: (event) => this.handleInterruption(event),
      });
    } catch (captureError) {
      debug("capture start failed", captureError);
      // Tear the half-started source down rather than just dropping the
      // reference — a native source that failed mid-start may still hold
      // plugin listeners, and a retry would double every chunk.
      if (this.capture === capture) this.capture = null;
      if (this.isSuperseded(generation)) return this.abandonStart(capture);
      void capture.stop().catch(() => undefined);
      this.failCapture();
      return;
    }
    if (this.isSuperseded(generation)) {
      if (this.capture === capture) this.capture = null;
      return this.abandonStart(capture);
    }

    if (!this.config.failFastOnFirstConnect) {
      void this.establishSession();
      return;
    }

    // Fail-fast: surface a first-connect failure to the caller instead of
    // silently retrying, so it can degrade to another engine. Everything
    // after this point — drops, rotations — still reconnects normally.
    const connected = await this.establishSession({ failFast: true });
    if (connected) return;

    // establishSession bumps the generation exactly once; anything beyond
    // that means a cancel (or dispose) landed while we were connecting. That
    // distinction matters: the caller must not fall back to another engine
    // for a session the user already dismissed.
    const cancelled = this.isSuperseded(generation + 1);
    this.resetToIdleState();
    throw cancelled ? new VoiceCancelledError() : new VoiceConnectFailedError();
  }

  /** True once a cancel/dispose has invalidated the generation `start` claimed. */
  private isSuperseded(generation: number): boolean {
    return this.disposed || this.sessionSeq !== generation;
  }

  /** Common teardown for a start whose mic never came up. */
  private failCapture(): void {
    this.capture = null;
    this.errorMessage = "Could not access the microphone.";
    this.setPhase("error");
  }

  /**
   * Give up a start that was cancelled while its mic was coming up, without
   * touching controller state — by now it belongs to idle or to a newer
   * start. `capture` is null when the source never materialized.
   */
  private abandonStart(capture: CaptureSource | null): void {
    void capture?.stop().catch(() => undefined);
    if (this.config.failFastOnFirstConnect) {
      throw new VoiceCancelledError();
    }
  }

  /** Recover a persisted draft straight into review (its audio is gone). */
  restoreDraft(draft: VoiceDraftV1): void {
    if (this.phase !== "idle" || this.disposed) return;
    this.draftId = draft.draftId;
    this.draftCreatedAt = draft.createdAt;
    this.draftOwned = true;
    this.workspaceScopeId = draft.workspaceScopeId;
    this.accumulator.restore({
      committed: draft.committedSegments,
      live: draft.liveText,
    });
    this.reviewText = draft.reviewText ?? null;
    this.recordedMs = draft.totalDurationSec * 1000;
    this.setPhase("review");
  }

  pause(): void {
    if (!this.isRecordingPhase()) return;
    this.pauseReason = "user";
    this.setMutedInternal(true);
    this.setPhase("paused");
    void this.flushPersist();
  }

  async resume(): Promise<void> {
    if (this.phase !== "paused") return;
    // An interruption tears the native engine down, so unmuting alone would
    // leave a dead mic behind a "recording" UI. User pauses only mute, and
    // rebuilding there would cost a needless gap — so only rebuild here.
    if (this.pauseReason === "interruption" && this.capture) {
      try {
        await this.capture.resume();
      } catch (resumeError) {
        debug("capture resume failed", resumeError);
        this.errorMessage =
          "Could not restart the microphone. Try again in a moment.";
        this.notify();
        return;
      }
    }
    this.pauseReason = null;
    this.errorMessage = null;
    this.setMutedInternal(false);
    if (this.active?.socket.isOpen) {
      this.setPhase("live");
      this.pump();
    } else {
      this.setPhase("reconnecting");
      if (!this.reconnectTimer) void this.attemptReconnect();
    }
  }

  stop(): void {
    if (
      !this.isRecordingPhase() &&
      this.phase !== "connecting" &&
      this.phase !== "paused"
    ) {
      return;
    }
    this.stopRequested = true;
    this.clearTimer("rotateWatchTimer");
    this.clearTimer("reconnectTimer");
    this.clearTimer("rotateRetryTimer");
    void this.stopCapture();

    if (this.active?.socket.isOpen) {
      this.setPhase("finalizing");
      // Overall bound on drain + flush + polish; on expiry the accumulated
      // text ships as-is. A large post-reconnect backlog that can't drain in
      // time loses only *un-transcribed* audio — never text already shown.
      this.finalizeTimer = this.clock.setTimeout(() => {
        this.finalizeTimer = null;
        debug("finalize timed out; rescuing accumulated text");
        this.accumulator.commitLive("drop_rescue");
        this.goReview();
      }, this.config.finalizeTimeoutMs);
      this.sendStopWhenDrained();
    } else {
      // Disconnected: the live text was already committed at the drop.
      this.accumulator.commitLive("drop_rescue");
      this.goReview();
    }
  }

  /** Abandon the recording. UI must confirm first — this clears the draft. */
  async cancel(): Promise<void> {
    this.stopRequested = true;
    this.active?.socket.sendJson({ type: "cancel" });
    await this.storage.clear();
    // Full reset, not a partial one: the ring buffer, replay cursor, and mute
    // flag must go too, or the next recording replays the discarded audio (or
    // starts muted, when cancelled from a paused state).
    this.resetToIdleState();
  }

  /** The transcript reached its destination — the draft is no longer ours. */
  async markDelivered(): Promise<void> {
    await this.storage.clear();
    this.resetToIdleState();
  }

  /** Explicit user discard from review (UI confirms). */
  async discard(): Promise<void> {
    await this.storage.clear();
    this.resetToIdleState();
  }

  /** Dismiss a terminal error back to idle (nothing recorded survives it). */
  acknowledgeError(): void {
    if (this.phase !== "error") return;
    this.resetToIdleState();
  }

  /** Persist a final snapshot (used by the app-background listener). */
  async flushPersist(): Promise<void> {
    this.clearTimer("persistTimer");
    if (!this.accumulator.hasText() && !this.reviewText) return;
    const draft: VoiceDraftV1 = {
      version: 1,
      draftId: this.draftId,
      createdAt: this.draftCreatedAt,
      updatedAt: this.clock.now(),
      workspaceScopeId: this.workspaceScopeId,
      ...(() => {
        const snap = this.accumulator.snapshot();
        return { committedSegments: snap.committed, liveText: snap.live };
      })(),
      totalDurationSec: Math.floor(this.recordedMs / 1000),
      status: this.phase === "review" ? "review" : "recording",
      ...(this.reviewText !== null ? { reviewText: this.reviewText } : {}),
    };
    try {
      await this.storage.save(draft);
    } catch (persistError) {
      debug("draft persist failed", persistError);
    }
  }

  /** Unmount without losing state: tear down audio + sockets, keep the draft. */
  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    // Only touch storage for a draft THIS controller owns — a freshly
    // mounted-and-abandoned controller must not clobber a stored draft the
    // user hasn't dealt with yet.
    if (this.draftOwned) {
      if (this.accumulator.hasText() || this.reviewText) {
        void this.flushPersist();
      } else {
        void this.storage.clear();
      }
    }
    this.teardownSockets();
    this.clearAllTimers();
    void this.stopCapture();
    this.listeners.clear();
  }

  // ----------------------------------------------------------- chunk intake

  private handleChunk(chunk: CaptureChunk): void {
    // `idle` covers a source that outlived the start it belonged to: a cancel
    // resets state but cannot un-emit a chunk already in flight.
    if (this.disposed || this.stopRequested || this.phase === "idle") return;
    this.ring.push(chunk);
    this.recordedMs += chunkDurationMs(chunk);
    this.updateLevels(chunk);

    if (this.active) {
      this.pending.push(chunk);
      this.pump();
    } else {
      this.checkBufferExhaustion();
    }

    this.schedulePersist();
    // Elapsed ticks once a second; text/phase changes notify on their own.
    const sec = Math.floor(this.recordedMs / 1000);
    if (sec !== this.lastNotifiedSec) {
      this.lastNotifiedSec = sec;
      this.notify();
    }

    const cap = this.config.maxTotalRecordingSec;
    if (cap !== undefined && this.recordedMs >= cap * 1000) {
      debug("total recording cap reached", { cap });
      this.onMaxDurationReached?.();
      this.stop();
    }
  }

  /** Fired when `maxTotalRecordingSec` stops the recording. */
  onMaxDurationReached?: () => void;

  /** Send queued chunks, gated on socket backpressure. Also runs during the
   * finalize drain, so no stopRequested guard here. */
  private pump(): void {
    const session = this.active;
    if (!session || !session.socket.isOpen) return;
    while (this.pending.length > 0) {
      if (session.socket.bufferedAmount > this.config.replayMaxBufferedBytes) {
        if (!this.pumpRetryTimer) {
          this.pumpRetryTimer = this.clock.setTimeout(() => {
            this.pumpRetryTimer = null;
            this.pump();
          }, 100);
        }
        return;
      }
      const chunk = this.pending.shift()!;
      session.socket.sendAudio(chunk.pcm);
      session.sentUpToMs = chunk.timestampMs + chunkDurationMs(chunk);
    }
  }

  // ------------------------------------------------------ session lifecycle

  /** Resolves true once a session is live. `failFast` reports a first-connect
   * failure to the caller instead of entering the reconnect loop. */
  private async establishSession(
    opts: { failFast?: boolean } = {},
  ): Promise<boolean> {
    const seq = ++this.sessionSeq;
    let socket: VoiceSocket;
    try {
      socket = await this.connectAndAwaitReady(seq);
    } catch (connectError) {
      if (this.isStale(seq)) return false;
      debug("session connect failed", connectError);
      if (opts.failFast) return false;
      this.enterReconnecting();
      return false;
    }
    if (this.isStale(seq)) {
      socket.close();
      return false;
    }

    this.active = {
      socket,
      seq,
      connectedAtWall: this.clock.now(),
      startCursorMs: this.nextReplayFromMs,
      sentUpToMs: this.nextReplayFromMs,
      lastTranscriptSentUpToMs: this.nextReplayFromMs,
      gotAnyTranscript: false,
    };
    this.pending = this.ring.sliceFrom(this.nextReplayFromMs);
    this.reconnectAttempt = 0;
    this.installSteadyStateHandlers(this.active);
    // The socket can die between `ready` and the handler swap — its close
    // event would have fired into the (already settled) connect handlers.
    if (!socket.isOpen) {
      this.handleActiveDrop(this.active);
      return false;
    }
    this.startPing();
    this.startRotateWatch();

    // Buffer-exhaustion pauses stay paused after reconnect: audio during the
    // outage is gone, and silently resuming would hide that gap. User pauses
    // also stay paused — the socket just sits ready.
    if (this.pauseReason) {
      this.setPhase("paused");
    } else {
      this.setPhase("live");
    }
    this.pump();
    return true;
  }

  /** Construct a socket and wait for the server's `ready` frame. */
  private connectAndAwaitReady(seq: number): Promise<VoiceSocket> {
    return new Promise<VoiceSocket>((resolve, reject) => {
      let settled = false;
      let socket: VoiceSocket | null = null;
      const timeout = this.clock.setTimeout(() => {
        settle(() => {
          socket?.close();
          reject(new Error("voice session connect timed out"));
        });
      }, this.config.connectTimeoutMs);
      const settle = (fn: () => void) => {
        if (settled) return;
        settled = true;
        this.connectingSocket = null;
        this.clock.clearTimeout(timeout);
        fn();
      };

      this.createSocket().then(
        (created) => {
          socket = created;
          // Tracked so a cancel mid-connect can close it immediately rather
          // than leaving start() pending until the connect timeout.
          this.connectingSocket = created;
          if (this.isStale(seq)) {
            settle(() => {
              created.close();
              reject(new Error("stale connect attempt"));
            });
            return;
          }
          created.setHandlers({
            onFrame: (frame) => {
              if (frame.type === "ready") {
                settle(() => resolve(created));
              } else if (frame.type === "error") {
                settle(() => {
                  created.close();
                  reject(
                    new Error(frame.message || "transcription unavailable"),
                  );
                });
              }
            },
            onClose: () => {
              settle(() =>
                reject(new Error("voice session socket closed early")),
              );
            },
          });
        },
        (err) =>
          settle(() =>
            reject(err instanceof Error ? err : new Error(String(err))),
          ),
      );
    });
  }

  private installSteadyStateHandlers(session: ActiveSession): void {
    session.socket.setHandlers({
      onFrame: (frame) => this.handleActiveFrame(session, frame),
      onClose: () => this.handleActiveClose(session),
    });
  }

  private handleActiveFrame(
    session: ActiveSession,
    frame: TranscriptionServerFrame,
  ): void {
    if (this.active !== session || this.disposed) return;
    switch (frame.type) {
      case "transcript":
        this.accumulator.setLive(frame.text ?? "");
        // Advance the drop-replay watermark toward the send high-water mark,
        // capped per frame: during a catch-up replay `sentUpToMs` races far
        // ahead of what the server has actually transcribed, and stamping it
        // directly could make a subsequent drop skip audio whose text never
        // arrived. Bounded advancement keeps the watermark honest (frames
        // stream continuously while the server churns through backlog).
        session.lastTranscriptSentUpToMs = Math.min(
          session.sentUpToMs,
          session.lastTranscriptSentUpToMs + 2000,
        );
        session.gotAnyTranscript = true;
        this.schedulePersist();
        this.notify();
        break;
      case "session_final":
        this.handleActiveFinal(session, frame);
        break;
      case "error":
        // Terminal with nothing to finalize server-side; the socket is about
        // to close. Treat exactly like a drop — capture keeps rolling.
        debug("server error frame", frame.message);
        this.handleActiveDrop(session);
        break;
      default:
        break;
    }
  }

  private handleActiveFinal(
    session: ActiveSession,
    frame: TranscriptionServerFrame,
  ): void {
    const text = frame.text ?? "";
    debug("session_final", { reason: frame.reason, length: text.length });

    if (this.phase === "finalizing") {
      this.clearTimer("finalizeTimer");
      // Polished final replaces the live tail; an empty final with visible
      // text falls back to the accumulated live (no-loss guarantee).
      if (text) this.accumulator.setLive(text);
      this.accumulator.commitLive(
        frame.reason === "error" ? "drop_rescue" : "stop",
      );
      this.goReview();
      return;
    }

    if (frame.reason === "max_duration") {
      // The server's 30-minute timer beat our proactive rotation (or the
      // pre-warm kept failing). Fold the final and chain a fresh session
      // from the flush point — a clean seam, so no rewind and no dedupe.
      if (text) this.accumulator.setLive(text);
      this.accumulator.commitLive("max_duration");
      this.detachActive(session);
      this.nextReplayFromMs = session.sentUpToMs;
      this.setPhase("rotating");
      void this.establishSession();
      return;
    }

    // reason "error" (upstream died) with partial text, or an unexpected
    // "stop" we never asked for: keep the text, then behave like a drop so
    // recording continues.
    if (text) this.accumulator.setLive(text);
    this.handleActiveDrop(session);
  }

  private handleActiveClose(session: ActiveSession): void {
    if (this.active !== session || this.disposed) return;
    if (this.phase === "finalizing") {
      this.clearTimer("finalizeTimer");
      this.accumulator.commitLive("drop_rescue");
      this.goReview();
      return;
    }
    this.handleActiveDrop(session);
  }

  /** Socket lost mid-recording: fold text, keep capturing, reconnect+replay. */
  private handleActiveDrop(session: ActiveSession): void {
    this.detachActive(session);
    this.accumulator.commitLive("drop_rescue");

    // Rewind ~1s behind the last transcript so in-flight audio is re-fed;
    // the accumulator's seam dedupe cleans up any re-transcribed overlap.
    const rewound = session.gotAnyTranscript
      ? Math.max(
          session.startCursorMs,
          session.lastTranscriptSentUpToMs - this.config.dropReplayRewindMs,
        )
      : session.startCursorMs;
    this.nextReplayFromMs = this.ring.isExhaustedFor(rewound)
      ? (this.ring.oldestTimestampMs ?? rewound)
      : rewound;

    this.enterReconnecting();
  }

  private detachActive(session: ActiveSession): void {
    if (this.active !== session) return;
    this.active = null;
    this.pending = [];
    this.clearTimer("pingTimer");
    this.clearTimer("rotateWatchTimer");
    this.clearTimer("pumpRetryTimer");
    session.socket.close();
  }

  private enterReconnecting(): void {
    if (this.stopRequested || this.disposed) return;
    this.disconnectedAtWall = this.disconnectedAtWall ?? this.clock.now();
    if (!this.pauseReason) this.setPhase("reconnecting");
    const attempt = this.reconnectAttempt;
    const backoffs = this.config.reconnectBackoffMs;
    const delay = backoffs[Math.min(attempt, backoffs.length - 1)];
    this.reconnectAttempt = attempt + 1;
    this.notify();
    this.reconnectTimer = this.clock.setTimeout(() => {
      this.reconnectTimer = null;
      void this.attemptReconnect();
    }, delay);
  }

  private disconnectedAtWall: number | null = null;

  private async attemptReconnect(): Promise<void> {
    if (this.stopRequested || this.disposed || this.active) return;
    await this.establishSession();
    if (this.active) this.disconnectedAtWall = null;
  }

  // --------------------------------------------------------------- rotation

  private startRotateWatch(): void {
    this.clearTimer("rotateWatchTimer");
    this.rotateWatchTimer = this.clock.setInterval(() => {
      const session = this.active;
      if (!session || this.phase !== "live") return;
      // A failed pre-warm sets a cooldown so we don't thrash reconnects at
      // 1Hz; the server's own max_duration final is the ultimate backstop.
      if (this.rotateRetryTimer) return;
      const wallSec = (this.clock.now() - session.connectedAtWall) / 1000;
      if (wallSec >= this.config.hardRotateSec) {
        void this.beginRotation("hard");
      } else if (wallSec >= this.config.softRotateSec && this.isSilent()) {
        void this.beginRotation("soft");
      }
    }, 1000);
  }

  private rotationInFlight = false;

  /**
   * Pre-warm rotation: open session B while A keeps transcribing, cut audio
   * over at B's `ready`, then flush A. Zero audio is skipped at the seam —
   * everything before the cut goes to A (covered by its stop-flush),
   * everything after queues for B.
   */
  private async beginRotation(kind: "soft" | "hard"): Promise<void> {
    if (this.rotationInFlight || !this.active || this.phase !== "live") return;
    this.rotationInFlight = true;
    debug("rotation begin", { kind });
    this.setPhase("rotating");

    const seq = ++this.sessionSeq;
    let newSocket: VoiceSocket;
    try {
      newSocket = await this.connectAndAwaitReady(seq);
    } catch (rotateError) {
      this.rotationInFlight = false;
      if (this.isStale(seq) || !this.active) return;
      debug("pre-warm rotation failed; staying on old session", rotateError);
      this.setPhase("live");
      // Cooldown before the rotate watch tries again; the server's
      // max_duration final is the backstop if pre-warms keep failing.
      this.clearTimer("rotateRetryTimer");
      this.rotateRetryTimer = this.clock.setTimeout(() => {
        this.rotateRetryTimer = null;
      }, this.config.rotateRetryMs);
      return;
    }

    const old = this.active;
    if (!old || this.isStale(seq)) {
      this.rotationInFlight = false;
      newSocket.close();
      return;
    }

    // Cut: audio ≤ old.sentUpToMs belongs to A; everything after goes to B.
    const cutMs = old.sentUpToMs;
    this.active = null;
    this.clearTimer("pingTimer");
    this.clearTimer("rotateWatchTimer");
    this.clearTimer("pumpRetryTimer");

    // Freeze A's text so B's transcripts can't clobber it, then drain A.
    const provisionalIndex = this.accumulator.beginProvisional("rotation");
    const drain: DrainingSession = {
      socket: old.socket,
      seq: old.seq,
      provisionalIndex,
      timer: this.clock.setTimeout(() => {
        // A's final never arrived; its provisional text stands.
        debug("rotation drain timed out");
        this.settleDraining(drain, null);
      }, this.config.finalizeTimeoutMs),
    };
    this.draining = drain;
    old.socket.setHandlers({
      onFrame: (frame) => this.handleDrainingFrame(drain, frame),
      onClose: () => this.settleDraining(drain, null),
    });
    old.socket.sendJson({ type: "stop" });

    // Bring up B as the active session.
    this.nextReplayFromMs = cutMs;
    this.active = {
      socket: newSocket,
      seq,
      connectedAtWall: this.clock.now(),
      startCursorMs: cutMs,
      sentUpToMs: cutMs,
      lastTranscriptSentUpToMs: cutMs,
      gotAnyTranscript: false,
    };
    this.pending = this.ring.sliceFrom(cutMs);
    this.installSteadyStateHandlers(this.active);
    this.rotationInFlight = false;
    if (!newSocket.isOpen) {
      this.handleActiveDrop(this.active);
      return;
    }
    this.startPing();
    this.startRotateWatch();
    this.setPhase(this.pauseReason ? "paused" : "live");
    this.pump();
  }

  private handleDrainingFrame(
    drain: DrainingSession,
    frame: TranscriptionServerFrame,
  ): void {
    if (this.draining !== drain) return;
    if (frame.type === "transcript") {
      // The flush can still refine A's text; keep the frozen segment current.
      if (drain.provisionalIndex !== null && frame.text) {
        this.accumulator.updateProvisional(drain.provisionalIndex, frame.text);
        this.notify();
      }
    } else if (frame.type === "session_final") {
      this.settleDraining(drain, frame.text ?? null);
    }
  }

  private settleDraining(
    drain: DrainingSession,
    finalText: string | null,
  ): void {
    if (this.draining !== drain) return;
    this.draining = null;
    this.clock.clearTimeout(drain.timer);
    if (drain.provisionalIndex !== null) {
      this.accumulator.resolveProvisional(
        drain.provisionalIndex,
        finalText ?? "",
        "rotation",
      );
    }
    drain.socket.close();
    this.schedulePersist();
    this.notify();
  }

  // -------------------------------------------------------------- finishing

  private sendStopWhenDrained(): void {
    const session = this.active;
    if (!session || !session.socket.isOpen) return;
    if (this.pending.length === 0) {
      session.socket.sendJson({ type: "stop" });
      return;
    }
    this.pump();
    this.clock.setTimeout(() => this.sendStopWhenDrained(), 100);
  }

  private goReview(): void {
    this.teardownSockets();
    this.clearAllTimers();
    void this.stopCapture();
    this.reviewText = this.accumulator.fullText();
    this.setPhase("review");
    void this.flushPersist();
  }

  private resetToIdleState(): void {
    // Invalidate any in-flight connect/rotation by generation. This reset
    // clears `stopRequested`, so the sequence number is the only thing left
    // marking those attempts stale — without the bump, a socket that becomes
    // `ready` after a cancel would install itself and flip the phase back to
    // live with capture already torn down, wedging the screen (start() only
    // runs from idle).
    this.sessionSeq++;
    this.teardownSockets();
    this.clearAllTimers();
    // Unmute before dropping the source: the native one proxies mute to a
    // plugin singleton, so leaving it muted could carry into the next take.
    this.setMutedInternal(false);
    void this.stopCapture();
    this.accumulator.reset();
    this.reviewText = null;
    this.recordedMs = 0;
    this.resetLevels();
    this.pauseReason = null;
    this.errorMessage = null;
    this.reconnectAttempt = 0;
    this.nextReplayFromMs = 0;
    this.ring.clear();
    this.pending = [];
    this.stopRequested = false;
    this.disconnectedAtWall = null;
    this.setPhase("idle");
  }

  // ---------------------------------------------------- pause / interruption

  private handleInterruption(event: {
    phase: "began" | "ended";
    shouldResume: boolean;
  }): void {
    if (this.disposed || this.stopRequested) return;
    if (event.phase === "began") {
      if (this.pauseReason === "user") return; // already paused by the user
      this.pauseReason = "interruption";
      if (this.isRecordingPhase()) this.setPhase("paused");
      void this.flushPersist();
    } else if (event.phase === "ended" && event.shouldResume) {
      if (this.pauseReason !== "interruption") return;
      this.pauseReason = null;
      if (this.phase === "paused") {
        if (this.active?.socket.isOpen) {
          this.setPhase("live");
          this.pump();
        } else {
          this.setPhase("reconnecting");
          if (!this.reconnectTimer) void this.attemptReconnect();
        }
      }
    }
  }

  private checkBufferExhaustion(): void {
    if (this.pauseReason || this.active) return;
    const newestEnd = this.ring.newestEndMs;
    if (newestEnd === null) return;
    const pendingSpan = newestEnd - this.nextReplayFromMs;
    if (
      pendingSpan >=
      this.config.ringBufferMs - this.config.exhaustionMarginMs
    ) {
      debug("ring buffer nearly exhausted; pausing capture");
      this.pauseReason = "buffer_exhausted";
      this.setMutedInternal(true);
      this.setPhase("paused");
      void this.flushPersist();
    }
  }

  private setMutedInternal(muted: boolean): void {
    this.muted = muted;
    this.capture?.setMuted(muted);
  }

  // ------------------------------------------------------------------ levels

  private updateLevels(chunk: CaptureChunk): void {
    const samples = new Int16Array(chunk.pcm);
    let sumSquares = 0;
    for (let i = 0; i < samples.length; i++) {
      const value = samples[i] / 32768;
      sumSquares += value * value;
    }
    const rms = samples.length ? Math.sqrt(sumSquares / samples.length) : 0;
    // Same log scaling as useAudioRecording/useLiveTranscription so the
    // waveform feel matches the composer surfaces.
    const logLevel = rms > 0 ? Math.log10(rms * 9 + 1) : 0;
    this.level = Math.max(0.05, Math.min(1, logLevel * 2.4));

    const now = this.clock.now();
    this.rmsSamples.push({ at: now, rms });
    const horizon = now - Math.max(2000, this.config.silenceWindowMs * 2);
    while (this.rmsSamples.length && this.rmsSamples[0].at < horizon) {
      this.rmsSamples.shift();
    }

    this.levelWindowPeak = Math.max(this.levelWindowPeak, this.level);
    this.levelWindowMs += chunkDurationMs(chunk);
    if (this.levelWindowMs >= LEVEL_SAMPLE_MS) {
      this.levelHistory.push(this.levelWindowPeak);
      if (this.levelHistory.length > LEVEL_HISTORY_LENGTH) {
        this.levelHistory.shift();
      }
      this.levelWindowMs = 0;
      this.levelWindowPeak = 0;
    }
  }

  private isSilent(): boolean {
    const now = this.clock.now();
    const windowStart = now - this.config.silenceWindowMs;
    const recent = this.rmsSamples.filter((sample) => sample.at >= windowStart);
    if (!recent.length) return false;
    return recent.every((sample) => sample.rms < this.config.silenceRms);
  }

  private resetLevels(): void {
    this.level = 0;
    this.levelHistory = [];
    this.levelWindowMs = 0;
    this.levelWindowPeak = 0;
    this.rmsSamples = [];
  }

  // ----------------------------------------------------------- housekeeping

  private draftCreatedAt = Date.now();
  private draftOwned = false;

  private schedulePersist(): void {
    if (this.persistTimer || this.disposed) return;
    this.persistTimer = this.clock.setTimeout(() => {
      this.persistTimer = null;
      void this.flushPersist();
    }, this.config.persistThrottleMs);
  }

  private startPing(): void {
    this.clearTimer("pingTimer");
    this.pingTimer = this.clock.setInterval(() => {
      this.active?.socket.sendJson({ type: "ping" });
    }, this.config.pingIntervalMs);
  }

  private bufferSecondsRemaining(): number | null {
    if (this.active || this.phase === "idle" || this.phase === "review") {
      return null;
    }
    const newestEnd = this.ring.newestEndMs;
    if (newestEnd === null) return null;
    const capacity =
      this.config.ringBufferMs -
      this.config.exhaustionMarginMs -
      (newestEnd - this.nextReplayFromMs);
    return Math.max(0, Math.ceil(capacity / 1000));
  }

  private isRecordingPhase(): boolean {
    return (
      this.phase === "live" ||
      this.phase === "rotating" ||
      this.phase === "reconnecting"
    );
  }

  private isStale(seq: number): boolean {
    return this.disposed || this.stopRequested || seq !== this.sessionSeq;
  }

  private async stopCapture(): Promise<void> {
    const capture = this.capture;
    this.capture = null;
    if (capture) {
      try {
        await capture.stop();
      } catch {
        // Already stopped.
      }
    }
  }

  private teardownSockets(): void {
    if (this.connectingSocket) {
      const connecting = this.connectingSocket;
      this.connectingSocket = null;
      connecting.close();
    }
    if (this.active) {
      const session = this.active;
      this.active = null;
      session.socket.setHandlers({ onFrame: () => {}, onClose: () => {} });
      session.socket.close();
    }
    if (this.draining) {
      const drain = this.draining;
      this.draining = null;
      this.clock.clearTimeout(drain.timer);
      drain.socket.setHandlers({ onFrame: () => {}, onClose: () => {} });
      drain.socket.close();
    }
    this.pending = [];
  }

  private clearTimer(
    key:
      | "pingTimer"
      | "rotateWatchTimer"
      | "reconnectTimer"
      | "finalizeTimer"
      | "pumpRetryTimer"
      | "persistTimer"
      | "rotateRetryTimer",
  ): void {
    const id = this[key];
    if (id === null) return;
    // Intervals and timeouts share the id space in both real and fake clocks.
    this.clock.clearTimeout(id);
    this.clock.clearInterval(id);
    this[key] = null;
  }

  private clearAllTimers(): void {
    this.clearTimer("pingTimer");
    this.clearTimer("rotateWatchTimer");
    this.clearTimer("reconnectTimer");
    this.clearTimer("finalizeTimer");
    this.clearTimer("pumpRetryTimer");
    this.clearTimer("persistTimer");
    this.clearTimer("rotateRetryTimer");
  }

  private setPhase(phase: VoicePhase): void {
    this.phase = phase;
    this.notify();
  }

  private notify(): void {
    this.snapshotCache = null;
    for (const listener of this.listeners) listener();
  }
}
